import React, { useEffect } from "react";
import ReactModal, { Props } from "react-modal";
import { Global, css } from "@emotion/react";
import {
  layout,
  flexbox,
  LayoutProps,
  FlexboxProps,
  ZIndexProps,
  space,
  SpaceProps,
  border,
  BorderProps,
  compose,
} from "styled-system";
import { styled, EmotionComponent } from "../../styled";
import { themeGet, IconCross, Button, Box } from "../../";
import { BoxProps } from "../Box";
import { ButtonProps } from "../Button";
import { IconProps } from "../Icon";

const StyledCloseButton = styled(Button)`
  border-radius: 0 0 50px 50px;

  &:hover:not(:disabled):not([disabled]) {
    border-radius: 0 0 50px 50px;
  }
`;

type StyleProps = LayoutProps &
  FlexboxProps &
  ZIndexProps &
  SpaceProps &
  BorderProps;

export type ModalProps = Omit<Props, "className"> & {
  className?: string;
  /** Default app element, so that screen readers don't see main content when modal is opened */
  appElement?: string;
  /** Handler for when the modal is closed */
  onRequestClose?: (event) => void;
  /** Should close when the escape key is pressed */
  shouldCloseOnEsc?: boolean;
  /** Should close when the overlay is clicked */
  shouldCloseOnOverlayClick?: boolean;
  /** Should we disable body scrolling while modal is open */
  shouldDisableBodyScrolling?: boolean;
  /** Show/hide the close button */
  showCloseButton?: boolean;
  /** Close button click event handler */
  onClose?: (event: React.MouseEvent) => void;
  /** Close button props */
  closeButtonProps?: ButtonProps;
  /** Close Cross Icon props */
  closeIconProps?: IconProps;
  /** Styled props for content */
  contentProps?: BoxProps;
  /** Enable default responsive styles */
  enableDefaultResponsiveStyles?: boolean;
  /** Styled props for React Modal */
} & StyleProps;

const styledProps = compose(layout, flexbox, space, border);

export const Modal: React.FC<ModalProps> = ({
  children,
  appElement,
  onRequestClose,
  shouldCloseOnEsc = true,
  shouldCloseOnOverlayClick = true,
  shouldDisableBodyScrolling = true,
  showCloseButton = true,
  onClose,
  closeButtonProps,
  closeIconProps,
  contentProps,
  enableDefaultResponsiveStyles = true,
  ...props
}) => {
  const onCloseHandler = onClose || onRequestClose;

  return (
    <StyledReactModal
      appElement={appElement}
      enableDefaultResponsiveStyles={enableDefaultResponsiveStyles}
      onRequestClose={onRequestClose}
      shouldCloseOnEsc={shouldCloseOnEsc}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      {...props}
    >
      {shouldDisableBodyScrolling && (
        <Global
          styles={css`
            .ReactModal__Body--open {
              overflow: hidden;
            }
          `}
        />
      )}
      {showCloseButton && (
        <Box position="absolute" top="-2px" right="18px" zIndex={1}>
          <StyledCloseButton
            variant="plain"
            sizeVariant="xs"
            display="inline"
            p="5px"
            pt="0px"
            width="28px"
            height="28px"
            backgroundColor="black"
            color="white"
            onClick={onCloseHandler}
            aria-label="Close"
            {...closeButtonProps}
          >
            <IconCross
              height="12px"
              width="12px"
              title="Close"
              {...closeIconProps}
            />
          </StyledCloseButton>
        </Box>
      )}

      <Box p="32px" fontWeight="light" fontSize="sm" {...contentProps}>
        {children}
      </Box>
    </StyledReactModal>
  );
};

type StylableReactModalProps = StyleProps &
  Omit<Props, "className"> & {
    className?: string;
    /** Default app element, so that screen readers don't see main content when modal is opened */
    appElement?: string;
    /** Enable default responsive styles */
    enableDefaultResponsiveStyles?: boolean;
  };

const StylableReactModal = ({
  appElement = "body",
  className,
  ...props
}: StylableReactModalProps) => {
  useEffect(() => {
    ReactModal.setAppElement(appElement);
  }, [appElement]);

  return (
    <ReactModal
      overlayClassName="ModalOverlay"
      className="ModalContent"
      portalClassName={className}
      {...props}
    />
  );
};

const StyledReactModal: EmotionComponent<StylableReactModalProps> = styled(
  StylableReactModal
)<StylableReactModalProps>`
  .ModalOverlay {
    position: fixed;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    background-color: ${themeGet("colors.ui.modalOverlay")};
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: ${(props) => props.zIndex || 100};
  }

  .ModalContent {
    position: relative;
    border: none;
    background: white;
    overflow: auto;
    outline: none;
    padding: 0px;
    border-radius: 5px;

    ${(props) =>
      props.enableDefaultResponsiveStyles
        ? css`
            max-height: 85%;
            height: 85%;
            width: 85%;
          `
        : ""}

    @media screen and (min-width: ${themeGet("breakpoints.xs")}) {
      ${(props) =>
        props.enableDefaultResponsiveStyles
          ? css`
              max-height: 100%;
              height: 100%;
              width: 100%;
            `
          : ""}
    }

    @media screen and (min-width: ${themeGet("breakpoints.md")}) {
      ${(props) =>
        props.enableDefaultResponsiveStyles
          ? css`
              height: auto;
              height: initial; /* set to be 'initial' if the browser supports, if not set it as 'auto'  */
              width: 50%;
            `
          : ""}
    }

    ${styledProps}
  }
`;

Modal.displayName = "Modal";

export default Modal;
